<template>
  <div>
    <div
      v-for="(_dateTime, index) in datetimesArray"
      :key="index"
      :class="ns.b()"
    >
      <span>{{ relativeTimes[index] }}</span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { useNamespace } from '@lit-element/hooks'
import { timeAgoProps } from './time-ago'

// 定义组件选项
defineOptions({
  name: 'LtTimeAgo',
})
// 使用命名空间
const ns = useNamespace('time-ago')
const props = defineProps(timeAgoProps)

// 存储相对时间的数组
const relativeTimes = ref<string[]>([])

// 将 datetimes 转换为数组
const datetimesArray = computed<Array<number | string>>(() => {
  if (Array.isArray(props.datetimes)) {
    return props.datetimes
  } else if (typeof props.datetimes === 'string') {
    // 如果是字符串，尝试解析为 JSON 数组
    try {
      const parsed = JSON.parse(props.datetimes)
      if (Array.isArray(parsed)) {
        return parsed
      }
    } catch (error) {
      console.error('Failed to parse datetimes as JSON array:', error)
      return [props.datetimes]
    }
  } else if (typeof props.datetimes === 'number') {
    // 如果是数字，放入数组
    return [props.datetimes]
  }
  return []
})

// 计算相对时间的方法
const calculateRelativeTime = (dateTime: number | string): string => {
  const now = Date.now()
  const then = new Date(dateTime).getTime()
  const diff = now - then

  const second = 1000
  const minute = 60 * 1000
  const hour = minute * 60
  const day = hour * 24
  const week = day * 7
  const month = day * 30
  const year = day * 365

  if (diff < second) {
    return '刚刚'
  } else if (diff < minute) {
    return `${Math.floor(diff / second)}秒前`
  } else if (diff < hour) {
    return `${Math.floor(diff / minute)}分钟前`
  } else if (diff < day) {
    return `${Math.floor(diff / hour)}小时前`
  } else if (diff < week) {
    return `${Math.floor(diff / day)}天前`
  } else if (diff < month) {
    return `${Math.floor(diff / week)}周前`
  } else if (diff < year) {
    return `${Math.floor(diff / month)}个月前`
  } else {
    return `${Math.floor(diff / year)}年前`
  }
}

// 更新相对时间的方法
const updateRelativeTimes = () => {
  relativeTimes.value = datetimesArray.value.map((dateTime) =>
    calculateRelativeTime(dateTime)
  )
}

// 组件挂载时更新相对时间，并设置定时器
onMounted(() => {
  updateRelativeTimes()
  const intervalId = setInterval(
    updateRelativeTimes,
    props.interval ?? 10000000
  )
  onUnmounted(() => {
    clearInterval(intervalId)
  })
})

// 监听 datetimes 属性的变化，更新相对时间
watch(
  () => props.datetimes,
  (newDatetimes) => {
    if (newDatetimes) {
      updateRelativeTimes()
    }
  }
)
</script>
